import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { captureFingerprint } from "./capture";
import {
  FingerprintCaptureError,
  type FingerprintCaptureOptions,
} from "./types";

export { SDK_VERSION } from "./version";

export interface UseFingerprintCaptureResult {
  capture: (overrides?: FingerprintCaptureOptions) => Promise<Blob>;
  cancel: () => void;
  isCapturing: boolean;
  image: Blob | null;
  error: FingerprintCaptureError | null;
}

export function useFingerprintCapture(
  defaultOptions: FingerprintCaptureOptions = {},
): UseFingerprintCaptureResult {
  const [isCapturing, setIsCapturing] = useState(false);
  const [image, setImage] = useState<Blob | null>(null);
  const [error, setError] = useState<FingerprintCaptureError | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const optionsRef = useRef(defaultOptions);
  optionsRef.current = defaultOptions;

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  useEffect(() => cancel, [cancel]);

  const capture = useCallback(
    async (overrides: FingerprintCaptureOptions = {}) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setIsCapturing(true);
      setError(null);

      try {
        const result = await captureFingerprint({
          ...optionsRef.current,
          ...overrides,
          signal: controller.signal,
        });
        setImage(result);
        return result;
      } catch (cause) {
        const captureError =
          cause instanceof FingerprintCaptureError
            ? cause
            : new FingerprintCaptureError(
                "PROCESSING_FAILED",
                "Fingerprint capture failed.",
                true,
                { cause },
              );
        setError(captureError);
        throw captureError;
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
        setIsCapturing(false);
      }
    },
    [],
  );

  return { capture, cancel, isCapturing, image, error };
}

export interface FingerprintCaptureProps {
  options?: FingerprintCaptureOptions;
  onComplete: (image: Blob) => void;
  onError?: (error: FingerprintCaptureError) => void;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  disabled?: boolean;
}

/**
 * A minimal trigger component. It opens the framework-neutral capture modal,
 * then returns the final PNG through `onComplete`.
 */
export function FingerprintCapture({
  options = {},
  onComplete,
  onError,
  children = "Capture fingerprint",
  className,
  style,
  disabled,
}: FingerprintCaptureProps) {
  const { capture, isCapturing } = useFingerprintCapture(options);

  const handleClick = async () => {
    try {
      onComplete(await capture());
    } catch (error) {
      onError?.(error as FingerprintCaptureError);
    }
  };

  return (
    <button
      type="button"
      className={className}
      style={style}
      disabled={disabled || isCapturing}
      aria-busy={isCapturing}
      onClick={handleClick}
    >
      {isCapturing ? "Opening camera…" : children}
    </button>
  );
}

export { FingerprintCaptureError } from "./types";
export type {
  CaptureMessages,
  CaptureTheme,
  FingerprintCaptureOptions,
  FingerprintErrorCode,
  FingerType,
  GuidanceCode,
} from "./types";
